// Copied with modification from:
// https://github.com/molefrog/wouter/blob/3ab8b987d50802ec8b08010302b5e22282fb57ab/packages/wouter/src/memory-location.js.

import mitt from 'mitt';
import { useCallback, useSyncExternalStore } from 'react';
import { useLocation as useBrowserLocation } from 'wouter';

type Location<T = Record<string, unknown>> = {
  path: string;
  state: T;
};

type NavigateFn = (path: string, ...args: any[]) => any;

const createMemoryLocation = () => {
  let currentLocation: Location = { path: '/', state: {} };

  const history = [currentLocation];

  const emitter = mitt();

  const navigate = (path, { replace = false, state = {} } = {}) => {
    currentLocation = { path, state };

    if (replace) {
      history.splice(-1, 1, currentLocation);
    } else {
      history.push(currentLocation);
    }

    emitter.emit('navigate', path);
  };

  const goBack = () => {
    if (history.length > 1) {
      history.pop();
      currentLocation = history[history.length - 1];
      emitter.emit('navigate', currentLocation.path);
    }
  };

  const subscribe = (callback) => {
    emitter.on('navigate', callback);
    return () => emitter.off('navigate', callback);
  };

  const useMemoryLocation: () => [string, NavigateFn] = () => [
    useSyncExternalStore(subscribe, () => currentLocation.path) as string,
    navigate,
  ];

  const useLocation = <T>() => {
    return useSyncExternalStore(
      subscribe,
      () => currentLocation as Location<T>,
    );
  };

  const useNavigate = () => {
    return useCallback((path: string | -1, ...args: any[]) => {
      if (path === -1) {
        goBack();
      } else {
        navigate(path, ...args);
      }
    }, []);
  };

  return {
    useLocation,
    useMemoryLocation,
    useNavigate,
  };
};

const {
  useMemoryLocation,
  useLocation: useMemoryRouterLocation,
  useNavigate: useMemoryRouterNavigate,
} = createMemoryLocation();

const useBrowserRouterNavigate = () => {
  const [, navigate] = useBrowserLocation();

  return useCallback(
    (path: string | -1, ...args: any[]) => {
      if (path === -1) {
        window.history.go(-1);
      } else {
        navigate(path, ...args);
      }
    },
    [navigate],
  );
};

const useBrowserRouterLocation = <T>() => {
  return {
    path: window.location.pathname,
    state: window.history.state,
  } as Location<T>;
};

const useNavigate = __STANDALONE_MODE__
  ? useBrowserRouterNavigate
  : useMemoryRouterNavigate;

const useLocation = __STANDALONE_MODE__
  ? useBrowserRouterLocation
  : useMemoryRouterLocation;

export { useLocation, useMemoryLocation, useNavigate };

// Utility to ensure type safety and remove repetition.
export function createNavigateStateOptions<LocationState>(
  state: LocationState,
): { state: LocationState } {
  return {
    state,
  };
}

export type ReferrerLocationState = {
  referrer?: string;
};

export function openInBlankWindow(url: string) {
  window.open(url, '_blank', 'noreferrer');
}
